<template>
  <div class="filter-card recall-filter-card">
    <div class="filter-card-header">
      <div class="filter-card-title">
        <i class="fa fa-refresh filter-icon"></i>
        <span>Recall Due Filter</span>
      </div>
      <b-dropdown variant="link" size="sm" no-caret right class="filter-menu">
        <template #button-content>
          <i class="fa fa-ellipsis-v text-muted"></i>
        </template>
        <b-dropdown-item @click="$emit('duplicate')">
          <i class="fa fa-copy mr-2"></i>Duplicate
        </b-dropdown-item>
        <b-dropdown-item variant="danger" @click="$emit('remove')">
          <i class="fa fa-trash mr-2"></i>Remove
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="filter-card-body">
      <div class="mb-3">
        <label class="filter-label">Recall Due Date <span class="required">*</span></label>
        <div class="filter-radio-group mb-2">
          <label class="filter-radio-option">
            <input type="radio" value="custom" v-model="timeframeMode" @change="onTimeframeModeChange" />
            <span>Custom range</span>
          </label>
          <label class="filter-radio-option">
            <input type="radio" value="relative" v-model="timeframeMode" @change="onTimeframeModeChange" />
            <span>In the last</span>
          </label>
        </div>

        <!-- Custom range mode -->
        <div v-if="timeframeMode === 'custom'" class="timeframe-custom-range">
          <div class="d-flex align-items-center gap-2">
            <label class="timeframe-date-label">From</label>
            <input
              type="date"
              v-model="localFilter.timeframe.from"
              :min="minCustomDate"
              :max="localFilter.timeframe.to || undefined"
              class="filter-form-input"
              @input="emitUpdate"
            />
            <label class="timeframe-date-label">To</label>
            <input
              type="date"
              v-model="localFilter.timeframe.to"
              :min="localFilter.timeframe.from || minCustomDate"
              class="filter-form-input"
              @input="emitUpdate"
            />
          </div>
        </div>

        <!-- Relative mode -->
        <div v-else class="timeframe-row">
          <span class="timeframe-label">In the last</span>
          <input
            v-model.number="localFilter.timeframe.value"
            type="number"
            min="1"
            class="filter-form-input timeframe-number"
            @input="emitUpdate"
          />
          <select
            v-model="localFilter.timeframe.unit"
            class="filter-form-input timeframe-unit"
            @change="emitUpdate"
          >
            <option value="months">Months</option>
            <option value="days">Days</option>
            <option value="years">Years</option>
          </select>
        </div>
      </div>

      <!-- Recall Type(s) filter (optional) — mirrors the Overdue Recall page's Recall Types picker -->
      <div class="mb-3">
        <label class="filter-label">Recall Type(s)</label>
        <multiselect
          v-model="selectedRecallTypes"
          :options="recallTypeOptions"
          :multiple="true"
          :searchable="true"
          :close-on-select="false"
          :show-labels="false"
          label="text"
          track-by="value"
          placeholder="All recall types (optional)"
          class="filter-multiselect"
          @input="onRecallTypesChange"
        />
      </div>

      <!-- Provider(s) filter (optional) -->
      <div class="mb-3">
        <label class="filter-label">Provider(s)</label>
        <multiselect
          v-model="selectedProviders"
          :options="providerOptions"
          :multiple="true"
          :searchable="true"
          :close-on-select="false"
          :show-labels="false"
          label="text"
          track-by="value"
          placeholder="All providers (optional)"
          class="filter-multiselect"
          @input="onProvidersChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: 'RecallFilterCard',
  components: { Multiselect },
  props: {
    value: {
      type: Object,
      required: true
    },
    providerOptions: {
      type: Array,
      default: () => []
    },
    recallTypeOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localFilter: JSON.parse(JSON.stringify(this.value)),
      selectedProviders: [],
      selectedRecallTypes: [],
      timeframeMode: this.value.timeframe?.mode === 'relative' ? 'relative' : 'custom'
    };
  },
  computed: {
    todayStr() {
      return new Date().toISOString().slice(0, 10);
    },
    minCustomDate() {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 4);
      return d.toISOString().slice(0, 10);
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.localFilter = JSON.parse(JSON.stringify(newVal));
        this.timeframeMode = newVal.timeframe?.mode === 'relative' ? 'relative' : 'custom';
        this.syncProviders();
        this.syncRecallTypes();
      },
      deep: true
    },
    // Unlike providers (already in the store at bootstrap), recall types are fetched over the
    // network — a reopened draft can render before they land, which would leave the saved
    // selection showing as empty chips. Re-sync once the options actually arrive.
    recallTypeOptions() {
      this.syncRecallTypes();
    }
  },
  created() {
    this.syncProviders();
    this.syncRecallTypes();
  },
  methods: {
    syncProviders() {
      this.selectedProviders = (this.localFilter.providers || [])
        .map(pn => this.providerOptions.find(o => o.value === pn))
        .filter(Boolean);
    },
    syncRecallTypes() {
      this.selectedRecallTypes = (this.localFilter.recallTypes || [])
        .map(rt => this.recallTypeOptions.find(o => o.value === rt))
        .filter(Boolean);
    },
    onTimeframeModeChange() {
      if (this.timeframeMode === 'custom') {
        this.localFilter.timeframe = { mode: 'custom', from: '', to: '' };
      } else {
        this.localFilter.timeframe = { mode: 'relative', value: 6, unit: 'months' };
      }
      this.emitUpdate();
    },
    onProvidersChange(selected) {
      this.localFilter.providers = selected.map(s => s.value);
      this.emitUpdate();
    },
    onRecallTypesChange(selected) {
      this.localFilter.recallTypes = selected.map(s => s.value);
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('input', { ...this.localFilter });
    }
  }
};
</script>

<style scoped lang="scss">
.recall-filter-card {
  border-left: 3px solid #10b981;
}

.filter-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: visible;
}

.filter-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0.875rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.filter-card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #334155;

  .filter-icon {
    color: #10b981;
    font-size: 0.875rem;
  }
}

.filter-menu ::v-deep .btn-link {
  padding: 0.125rem 0.375rem;
  color: #94a3b8;
  &:hover { color: #475569; }
}

.filter-card-body {
  padding: 0.875rem;
}

.filter-multiselect ::v-deep {
  .multiselect__tags {
    min-height: 34px;
    padding: 4px 40px 0 8px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.8125rem;
  }
  .multiselect__tag {
    background: #d1fae5;
    color: #065f46;
    border-radius: 4px;
    font-size: 0.75rem;
    padding: 2px 22px 2px 8px;
    margin-bottom: 2px;
  }
  .multiselect__tag-icon {
    &:after { color: #10b981; }
    &:hover { background: #a7f3d0; }
  }
  .multiselect__option--highlight {
    background: #10b981;
  }
  .multiselect__content-wrapper {
    z-index: 1060;
    border-radius: 6px;
    border-color: #e2e8f0;
    max-height: 200px;
  }
  .multiselect__placeholder {
    font-size: 0.8125rem;
    color: #94a3b8;
    padding-top: 0;
    margin-bottom: 0;
  }
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 0.375rem;
  display: block;

  .required {
    color: #ef4444;
  }
}

.filter-form-input {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.8125rem;
  outline: none;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.15);
  }
}

select.filter-form-input {
  height: 34px;
  line-height: 1.2;
  background: white;
  cursor: pointer;
}

.filter-radio-group {
  display: flex;
  gap: 16px;
}

.filter-radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 0;

  input[type="radio"] {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
    accent-color: #10b981;
  }
}

.timeframe-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.timeframe-label {
  font-size: 0.8125rem;
  color: #64748b;
  white-space: nowrap;
}

.timeframe-number {
  width: 60px;
}

.timeframe-unit {
  width: 100px;
}

.timeframe-custom-range {
  .d-flex {
    gap: 0.5rem;
  }

  input[type="date"] {
    width: 140px;
  }
}

.timeframe-date-label {
  font-size: 0.8125rem;
  color: #64748b;
  white-space: nowrap;
}
</style>
